import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Search, FileCheck, Shield, Eye, ArrowLeft, RefreshCw,
  CheckCircle, Clock, Smartphone, Pen, Calendar, User, Phone, Mail, Euro
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function InvoiceWarrantyHistoryPage() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [total, setTotal] = useState(0);
  
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchHistory();
  }, [searchTerm]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      params.append('limit', '50');
      
      const response = await axios.get(
        `${BACKEND_URL}/api/invoices/warranty/signatures/history?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setHistory(response.data.history || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Erreur lors du chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy HH:mm', { locale: fr });
    } catch {
      return dateStr.slice(0, 19).replace('T', ' ');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/invoices')}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
                <Shield className="w-8 h-8 text-green-400" />
                Historique Signatures Garantie
              </h1>
              <p className="text-white/60 mt-1">Système de preuves pour les factures avec garantie</p>
            </div>
          </div>
          
          <Button
            onClick={fetchHistory}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Search */}
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
            <Input
              type="text"
              placeholder="Rechercher par nom, téléphone ou numéro de facture..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="bg-green-500/20 backdrop-blur-md rounded-xl p-4 mb-6 flex items-center gap-4">
          <FileCheck className="w-8 h-8 text-green-400" />
          <div>
            <p className="text-white/60 text-sm">Total factures signées</p>
            <p className="text-2xl font-bold text-white">{total}</p>
          </div>
        </div>

        {/* Liste */}
        <div className="bg-white/10 backdrop-blur-md rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
              <p className="text-white/60">Chargement...</p>
            </div>
          ) : history.length === 0 ? (
            <div className="p-12 text-center">
              <Shield className="w-16 h-16 text-white/30 mx-auto mb-4" />
              <p className="text-white/60 text-lg">Aucune signature de garantie enregistrée</p>
              <p className="text-white/40 text-sm mt-2">
                Les signatures apparaîtront ici une fois que des clients auront signé les conditions de garantie sur leurs factures.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {history.map((item) => (
                <div 
                  key={item.invoice_id}
                  className="p-4 hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-green-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-white text-lg">{item.invoice_number}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            item.sale_type === 'Réparation' 
                              ? 'bg-blue-500/30 text-blue-300' 
                              : 'bg-purple-500/30 text-purple-300'
                          }`}>
                            {item.sale_type}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-white/60 text-sm">
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {item.client_name}
                          </span>
                          {item.client_phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              {item.client_phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-white font-bold text-lg flex items-center justify-end gap-1">
                        <Euro className="w-4 h-4" />
                        {item.total?.toFixed(2)}
                      </p>
                      <div className="flex items-center gap-2 mt-1 text-white/60 text-sm">
                        {item.signature.method === 'on_site' ? (
                          <Pen className="w-4 h-4 text-blue-400" />
                        ) : (
                          <Smartphone className="w-4 h-4 text-green-400" />
                        )}
                        <span>{item.signature.method === 'on_site' ? 'Sur place' : 'À distance (OTP)'}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-white/50 text-sm">
                      <Calendar className="w-4 h-4" />
                      <span>Signé le {formatDate(item.signature.date)}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/invoices/${item.invoice_id}`)}
                      className="text-white/70 hover:text-white hover:bg-white/10"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Voir la facture
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
